/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useMemo, useState, useContext } from 'react';
import { useInView } from 'react-intersection-observer';
import { CarouselContext, Slide, Slider } from 'pure-react-carousel';
import { useDebounce } from 'use-debounce';

import { ProcessedImage, UnprocessedImage } from './PropertyImages.types';
import { renderedData } from '../../organisms/SearchResults/searchResultsMainComponent';
import { DEFAULT_IMAGE } from '../../organisms/SearchResults/index.constant';
import { useConditionalSearchResultsRenderedDataSERP } from '../../hooks/lib/useConditionalRenderedData';

const isInvalidImageOriginal = (original: string) => original?.startsWith('?');

export const PropertyImages: FC<{
  images: UnprocessedImage[];
  getImgProcQueryString: string;
  imageDimensions: any;
  brandImageSrcUrl: string;
  fallbackImageSrc: string;
  propertyID: string | undefined;
  hotelName: string;
  setSlideCount: any;
  selectedImage: string;
  isBrandLogoPresent: boolean;
}> = ({
  images,
  getImgProcQueryString,
  imageDimensions,
  brandImageSrcUrl,
  fallbackImageSrc,
  propertyID,
  setSlideCount,
  hotelName = '',
  selectedImage,
  isBrandLogoPresent,
}) => {
  const carouselContext = useContext(CarouselContext);
  const { jsonContent }: any = useConditionalSearchResultsRenderedDataSERP(renderedData);
  const [currentSlide, setCurrentSlide] = useState(carouselContext.state.currentSlide);
  useEffect(() => {
    function onChange() {
      setCurrentSlide(carouselContext.state.currentSlide);
    }
    carouselContext.subscribe(onChange);
    return () => carouselContext.unsubscribe(onChange);
  }, [carouselContext]);

  const { ref: intersectionRef, inView } = useInView({
    threshold: 0.05,
    delay: 800,
    trackVisibility: true,
  });

  // const preloadItemPadding = 2; // TODO: Use this to determine preloading logic instead of hardcoding.
  const [zeroIndexImg, setZeroIndexImg] = useState<string>(
    isInvalidImageOriginal(images[0]?.original) ? '' : images[0].original
  );
  const [processedImages, setProcessedImages] = useState<ProcessedImage[]>([]);
  const [imagesToPreload, setImagesToPreload] = useState<string[]>([]);
  const [selectedImageRendition, setSelectedImageRendition] = useState<string>(selectedImage);

  const processedImagesIds = processedImages?.map(img => img.name);

  useEffect(() => {
    if (images && images.length > 0) {
      setZeroIndexImg(isInvalidImageOriginal(images[0].original) ? '' : images[0].original);
      // setProcessedImages([]);
      setImagesToPreload([]);
    }
  }, [images]);

  const preloadedImagesIds = processedImages
    .filter(img => getImgFileName(img.src) !== getImgFileName(zeroIndexImg + getImgProcQueryString))
    ?.map(img => img.name);

  const imagesIds = images?.map(img => img.name);

  // Function to get the file name from the URL to ensure unqiue imgs are displayed in carousel
  function getImgFileName(imgURL: string) {
    const fileName = imgURL ? imgURL?.split('?')?.[0]?.split(':')?.[1]?.split('/')?.slice(-1)?.[0].split('.')?.[0] : '';
    return fileName;
  }

  const getLocalSlideCount = useMemo(() => {
    // Get number of legitimate slides.
    return processedImages.filter(img => !img.failedToLoad).length;
  }, [processedImages]);
  const [localSlideCount] = useDebounce(getLocalSlideCount, 600);
  useEffect(() => {
    // If number of legitimate slides has changed, notify parent container.
    if (localSlideCount !== carouselContext.state.totalSlides) {
      setSlideCount(localSlideCount);
    }
  }, [localSlideCount]);

  const constructProcessedImage = (image: any, loadEager: boolean, failedToLoad: boolean) => {
    return {
      imageAltText: image.imageAltText ? image.imageAltText : image.name.replace(/-/g, ' '),
      // Rm spaces and make lowercase in case name is not formed like an ID.
      name: image.name,
      // If new, we always use the original.
      src: image.src ? image.src + getImgProcQueryString : image.original + getImgProcQueryString,
      original:
        image.original === zeroIndexImg ? zeroIndexImg + getImgProcQueryString : image.original + getImgProcQueryString,
      failedToLoad: failedToLoad,
      loading: loadEager ? 'eager' : 'lazy',
    };
  };

  const doPreloadManagement = async () => {
    // Find the id that isn't in processed images.
    const allImagesToPreload = imagesToPreload.filter(item => preloadedImagesIds.indexOf(item) <= -1);
    allImagesToPreload.forEach(async imageId => {
      const preloadImgObj = processedImages.find(el => el.name === imageId);
      const preloadOriginal = images.find(img => img.name === imageId)?.original ?? '';
      if (preloadImgObj && !isInvalidImageOriginal(preloadOriginal)) {
        const tryImage = new Image();
        tryImage.onload = () => {
          // log.debug(`load successful for ${propertyID}, ${preloadImgObj.name}`);
          setProcessedImages(prev => {
            const newPrev = [...prev];
            const modifyImgIndex = prev.findIndex(img => img.name === preloadImgObj.name);
            if (modifyImgIndex >= 0 && newPrev[modifyImgIndex]) {
              newPrev[modifyImgIndex].src = preloadImgObj.original;
              newPrev[modifyImgIndex].loading = 'eager';
              newPrev[modifyImgIndex].failedToLoad = false;
            }
            return [...newPrev];
          });
        };
        tryImage.onerror = () => {
          // log.debug(`load failed for ${propertyID}, ${preloadImgObj.name}`);
          const modifyImgIndex = processedImagesIds.indexOf(preloadImgObj.name);
          // If it's one of the top 2 or index 1 or 2, add another image to preload queue.
          if (
            modifyImgIndex === 1 ||
            modifyImgIndex === 2 ||
            modifyImgIndex === processedImagesIds.length - 1 ||
            modifyImgIndex === processedImagesIds.length - 2
          ) {
            const halfwayPoint = localSlideCount / 2;
            // If it was one of the first 4 to load, add another one.
            if (modifyImgIndex > halfwayPoint) {
              const nextToLoad = processedImages[modifyImgIndex - 1];
              const nextNextToLoad = processedImages[modifyImgIndex - 2];
              const nextFailedToLoad = nextToLoad.failedToLoad;
              const nextNextFailedToLoad = nextNextToLoad.failedToLoad;
              if (!nextFailedToLoad) {
                setImagesToPreload(prev => {
                  return [...prev, nextNextToLoad.name];
                });
              }
              if (nextFailedToLoad && !nextNextFailedToLoad) {
                setImagesToPreload(prev => {
                  return [...prev, nextNextToLoad.name];
                });
              }
            } else {
              const nextToLoad = processedImages[modifyImgIndex + 1];
              const nextNextToLoad = processedImages[modifyImgIndex + 2];
              const nextFailedToLoad = nextToLoad.failedToLoad;
              const nextNextFailedToLoad = nextNextToLoad.failedToLoad;
              if (!nextFailedToLoad) {
                setImagesToPreload(prev => {
                  return [...prev, nextNextToLoad.name];
                });
              }
              if (nextFailedToLoad && !nextNextFailedToLoad) {
                setImagesToPreload(prev => {
                  return [...prev, nextNextToLoad.name];
                });
              }
            }
          }
          setProcessedImages(prev => {
            const newPrev = [...prev];
            // const modifyImgIndex = processedImagesIds.indexOf(preloadImgObj.name);
            newPrev[modifyImgIndex].failedToLoad = true;

            return [...newPrev];
          });
        };
        tryImage.src = preloadImgObj.original;
      }
    });
  };

  useEffect(() => {
    // log.debug(`imagesToPreload changed for ${propertyID}`, imagesToPreload);
    if (imagesToPreload.length > 0) {
      doPreloadManagement();
    }
  }, [imagesToPreload]);

  const updateProcessedImages = () => {
    // Add processedImages once we have the full media query.
    if (images.length > 1 && processedImages.length < images.length) {
      setProcessedImages(prev => {
        // If in processed images, don't replace,
        // otherwise insert.
        const addToProcessedImages = images
          ?.map((item: any) => {
            return constructProcessedImage(
              { name: item.name, src: zeroIndexImg, original: item.original, imageAltText: item.imageAltText },
              true, // i === 0 || inView ? true : false,
              false
            );
          })
          .filter(item => processedImagesIds.indexOf(item.name) <= -1);
        return [...prev, ...addToProcessedImages];
      });
    }
    if (selectedImageRendition !== selectedImage) {
      setProcessedImages([]);
      setSelectedImageRendition(selectedImage);
    }
    // If inView, send the next 4 for preload processing.
    if (inView && images.length > 1) {
      // If none yet set to preload, send the first 4.
      if (imagesToPreload.length === 0) {
        setImagesToPreload([
          imagesIds[1],
          imagesIds[2],
          imagesIds[imagesIds.length - 2],
          imagesIds[imagesIds.length - 1],
        ]);
      }
      // Only send more images for preload if we haven't preloaded all of them.
      const imagesToPreloadDeDup =
        imagesToPreload.length && imagesToPreload.length > 0 ? [...new Set(imagesToPreload)] : [];
      // If we have loaded the initial 4 for padding.
      // And we are not on slide 0.
      // And images to preload minus duplicates is less than length of processed images.
      if (imagesToPreloadDeDup.length >= 4 && currentSlide !== 0 && imagesToPreloadDeDup.length < images.length - 1) {
        // log.debug(`updateProcessedImages inView for ${propertyID}, currentSlide changed`);
        // Go through processed images,
        // find the next one that's not processed.
        // Send that one for preprocessing.
        // Above half if current slide is between half of length and full length.
        const loadFromEnd = currentSlide > localSlideCount / 2;
        // If we are above half, load from the end,
        // If we are below half, load from the beginning
        // not 0, which has its own loading error handler, but 1.
        let nextPreload: string | null = null;
        let i = loadFromEnd ? localSlideCount - 1 : 1;
        for (i; loadFromEnd ? i > 0 : i < imagesIds.length - 1; loadFromEnd ? i-- : i++) {
          // If it's not undefined and not index zero image,
          // and it's not already in the preload list,
          // add it to the preload list, then break loop.
          if (
            nextPreload === null &&
            imagesIds[i] &&
            imagesIds[i] !== images[0].name &&
            imagesToPreloadDeDup.indexOf(imagesIds[i]) <= -1
          ) {
            nextPreload = imagesIds[i];
            // eslint-disable-next-line no-loop-func
            setImagesToPreload(prev => {
              return [...prev, imagesIds[i]];
            });
            // Break after adding only one (per nav click).
            break;
          }
        }
      }
    }
  };

  useEffect(() => {
    updateProcessedImages();
  }, [images, currentSlide, inView]);

  // Handle loading error for first image,
  // swap out with fallbacks if loading error occurs.
  const handleError = async (e: any): Promise<void> => {
    // If index is zero, handle like regular.
    e.cancelable && e.preventDefault();
    if (!e.defaultPrevented) {
      setZeroIndexImg(brandImageSrcUrl);
      // e.currentTarget.src = brandImageSrcUrl;
      e.cancelable && e.preventDefault();
    } else if (!e.isPropagationStopped()) {
      setZeroIndexImg(fallbackImageSrc);
      // e.currentTarget.src = fallbackImageSrc;
      e.stopPropagation();
    }
  };

  return (
    <div
      ref={intersectionRef}
      className="slider-wrapper"
      style={{
        backgroundImage: `${
          isBrandLogoPresent
            ? `linear-gradient( to bottom, rgba(0, 0, 0, 0) calc(100% - 7.25rem), rgba(0, 0, 0, 0) calc(100% - 7.25rem),
         rgba(0, 0, 0, 0) calc(100% - 7.25rem), rgba(0, 0, 0, 0) calc(100% - 7.25rem), rgba(0, 0, 0, 0.47) calc(100% - 4.13rem),
          rgba(0, 0, 0, 0.62) 100%) ,`
            : ''
        } url(${
          zeroIndexImg ? zeroIndexImg + getImgProcQueryString : brandImageSrcUrl ? brandImageSrcUrl : DEFAULT_IMAGE
        })`,
        width: '100%',
        height: imageDimensions.height,
        backgroundRepeat: 'round',
        backgroundSize: 'cover',
      }}
    >
      {(!processedImages || processedImages.length === 0) && !brandImageSrcUrl && !zeroIndexImg && (
        <div className="property-image unavailable-image-fallback" aria-label={jsonContent?.fallBackImageAltText}>
          <img
            className="property-image--img"
            src={fallbackImageSrc}
            alt={jsonContent?.fallBackImageAltText}
            height={imageDimensions.height}
            width={imageDimensions.width}
          />
          <div className="image-unavailable-text t-font-inverse-xs">{jsonContent?.imageUnavailableLabel}</div>
          <div className="scrim-component" />
        </div>
      )}
      <Slider
        aria-label={`carousel of property ${hotelName} images, slide count ${processedImages.length}, active image ${
          currentSlide + 1
        }`}
        role={processedImages.length > 1 ? 'listbox' : 'img'}
      >
        {processedImages.length > 1 &&
          processedImages?.map((litem: any, index: number) =>
            litem && !litem.failedToLoad && (index === 0 || !isInvalidImageOriginal(litem.original)) ? (
              <Slide
                className="property-image"
                index={index}
                key={`property-image-${propertyID}-${litem.name}-${index}`}
                aria-label={litem.imageAltText}
                aria-live="off"
              >
                <img
                  data-index={index}
                  data-name={litem.name}
                  className="property-image--img"
                  loading={litem.loading}
                  // condition to check if the image is zero index then adding the correspandin primary image/Brand Image/Fallback Imag
                  // If its not zero index then adding the original image link from the Image list array
                  src={
                    index === 0
                      ? // If the zero index image is not undefined, null, or empty, then adding the corresponding image link from the Image list array else adding the default image
                        zeroIndexImg !== undefined && zeroIndexImg !== null && zeroIndexImg !== ''
                        ? zeroIndexImg + getImgProcQueryString
                        : DEFAULT_IMAGE
                      : litem.original
                  }
                  alt={litem.imageAltText}
                  height={imageDimensions.height}
                  width={imageDimensions.width}
                  onError={index === 0 ? handleError : undefined}
                />
                <div className="scrim-component" />
              </Slide>
            ) : null
          )}
      </Slider>
    </div>
  );
};
